import React, { useState, useMemo } from 'react';
import { appraiseItems, compareMarkets, processItems } from '../api';
import MarketSelector from './MarketSelector';
import { HashLoader } from 'react-spinners'; // Import a spinner component

// Unified regex pattern for quantities that handles various number formats
const QUANTITY_PATTERN = String.raw`[,'\.\s\u00a0\xc2\xa0\d]+`;
// Pattern for item names that allows any characters except delimiters
const ITEM_NAME_PATTERN = String.raw`[^,\t\n][^,\t\n]*[^,\t\n]`;

// Protected patterns - items that should not be parsed for quantities
const PROTECTED_PATTERNS = [
  /^(?:Navy )?Cap Booster \d+$/,
  /^(?:Navy )?Cap Booster \d+ Blueprint$/
];

// Headers and labels to exclude from parsing
const EXCLUDED_HEADERS = new Set([
  'Components',
  'Minerals',
  'Planetary materials',
  'Items',
  'Datacores',
  'Optional items',
  'No item selected',
  'Total:',
  'Item\tRequired\tAvailable\tEst. Unit price\ttypeID'
]);

// Updated pattern formats with more precise handling of all formats
const INPUT_PATTERNS = {
  // Ship fitting format - must be first to take precedence
  shipFittingFormat: new RegExp(`^\\[(?<name>[^,]+),\\s*(?<description>[^\\]]+)\\]$`),
  
  // Module with attachment format - for both scripts and ammo
  moduleWithAttachment: new RegExp([
    `^(?<module>[^,\t\n]*[a-zA-Z][^,\t\n]*)`,  // Must end with a letter
    `\\s*,\\s*`,                                // Comma with optional spaces
    `(?<attachment>[^,\t\n]*[a-zA-Z][^,\t\n]*)$` // Must start with a letter
  ].join('')),

  // Basic formats
  itemSpaceQuantity: new RegExp(`^(?<name>${ITEM_NAME_PATTERN})\\s+(?<quantity>${QUANTITY_PATTERN})$`),
  quantitySpaceItem: new RegExp(`^(?<quantity>${QUANTITY_PATTERN})\\s+(?<name>${ITEM_NAME_PATTERN})$`),
  itemTabQuantity: new RegExp(`^(?<name>${ITEM_NAME_PATTERN})\\t(?<quantity>${QUANTITY_PATTERN})$`),
  quantityTabItem: new RegExp(`^(?<quantity>${QUANTITY_PATTERN})\\t(?<name>${ITEM_NAME_PATTERN})$`),
  
  // Placeholder patterns for x formats - actual x handling is done in parseItemLine
  itemXQuantity: new RegExp(`^\$`), // Matches nothing
  quantityXItem: new RegExp(`^\$`), // Matches nothing
  
  // Industry format
  industryFormat: new RegExp(`^(?<name>${ITEM_NAME_PATTERN})\\s+\\((?<quantity>\\d+)\\s*Units?\\)$`),
  
  // Format for numbers in middle of text (e.g. "Sperrylite 156.169 Uncommon Moon Asteroids" or "Isogen 562 500 Mineral")
  midNumberFormat: new RegExp(`^(?<name>[^0-9]+?)\\s+(?<quantity>[0-9][0-9,. ]*[0-9])(?:\\s+.*)?$`),
  
  // Materials list format (tab-separated with specific columns)
  materialsFormat: new RegExp([
    `^(?<name>${ITEM_NAME_PATTERN})`,
    `\\t(?<quantity>${QUANTITY_PATTERN})`,
    `\\t(?<available>${QUANTITY_PATTERN})`,
    `\\t(?<price>${QUANTITY_PATTERN})`,
    `\\t(?<typeId>${QUANTITY_PATTERN})$`
  ].join(''))
};

const BulkInputForm = ({ onResults, onAppraisalComplete, trackEvent, loading, setLoading  }) => {
  const [rawInput, setRawInput] = useState('');
  const [error, setError] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('jita');
  const [selectedMarket2, setSelectedMarket2] = useState('ualx');
  const [showYieldParams, setShowYieldParams] = useState(false);
  const [yieldParams, setYieldParams] = useState({
    Rm: 3, // T2 Rig
    Sec: 0.12, // Nullsec
    Sm: 0.055, // Tatara
    R: 5, // Max Reprocessing
    Re: 5, // Max Reprocessing Efficiency
    Op: 5, // Max Ore Processing
    Im: 0.04 // Best Implant
  });

  const normalizeItemName = (name) => {
    if (!name) return '';
  
    // Handle ship fitting format [ShipName, Fitting Description]
    const fittingMatch = name.match(/^\[(.*?),.*?\]$/);
    if (fittingMatch) {
      return fittingMatch[1].trim();
    }
    
    return name
      ?.trim()
      ?.replace(/\s+/g, ' ')  // Normalize multiple spaces to single space
      ?.replace(/^\s*\*\s*/, '')  // Remove leading asterisk
      ?.replace(/\s*\*\s*$/, '')  // Remove trailing asterisk
      ?.replace(/\s*\*(\s+|$)/g, '')  // Remove asterisk followed by spaces or end of string
      || '';
  };

  const normalizeQuantity = (quantityStr) => {
    if (!quantityStr) return 1;
    
    // Join all numbers and remove any non-digit characters
    const cleaned = quantityStr
      .replace(/[^\d\s]/g, '') // Remove anything that's not a digit or space
      .replace(/\s+/g, '')     // Remove all spaces
    
    const parsed = parseInt(cleaned, 10);
    return isNaN(parsed) ? 1 : Math.max(1, parsed);
  };

  // Try to parse using a specific format first
  const tryParseFormat = (line, format, pattern) => {
    const match = pattern.exec(line);
    if (match?.groups) {
      const { name, quantity } = match.groups;
      if (name && quantity) {
        return {
          name: normalizeItemName(name),
          quantity: normalizeQuantity(quantity)
        };
      }
    }
    return null;
  };

  const parseItemLine = (line) => {
    const trimmedLine = line.trim();
    if (!trimmedLine || EXCLUDED_HEADERS.has(trimmedLine)) {
      return null;
    }

    // Check if the item matches any protected patterns
    for (const pattern of PROTECTED_PATTERNS) {
      if (pattern.test(trimmedLine)) {
        return {
          name: trimmedLine,
          quantity: 1
        };
      }
    }

    // Check if it's a ship fitting line (enclosed in square brackets)
    if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
      const shipMatch = INPUT_PATTERNS.shipFittingFormat.exec(trimmedLine);
      if (shipMatch?.groups) {
        const { name } = shipMatch.groups;
        return {
          name: normalizeItemName(name),
          quantity: 1
        };
      }
    }

    // Only check for module with attachment if line contains comma and isn't a fitting
    const attachmentMatch = !trimmedLine.startsWith('[') && INPUT_PATTERNS.moduleWithAttachment.exec(trimmedLine);
    if (attachmentMatch?.groups) {
      const { module, attachment } = attachmentMatch.groups;
      return [
        { name: normalizeItemName(module), quantity: 1 },
        { name: normalizeItemName(attachment), quantity: 1 }
      ];
    }
  
    // Special handling for materials list format
    if (trimmedLine.includes('\t')) {
      const parts = trimmedLine.split('\t');
      if (parts.length >= 2) {
        const name = parts[0].trim();
        const quantity = normalizeQuantity(parts[1]);
        if (name && quantity) {
          return { name: normalizeItemName(name), quantity };
        }
      }
    }
  
// First try x-format parsing (must be an intentional quantity separator)
const hasValidXSeparator = /(?:^|\s)[,.\d]+\s*[xX]\s+|\s+[xX]\s*[,.\d]+(?:\s|$)/i.test(trimmedLine);
if (hasValidXSeparator) {
  let xMatch = trimmedLine.match(
    /^([,.\d\s]+?)\s*[xX](?:\s+|$)(.+)$|^(.+?)(?:^|\s+)[xX]\s*([,.\d\s]+)$/i
  );
  if (xMatch) {
    if (xMatch[1] && xMatch[2]) { // "quantity x item" format
      return {
        name: normalizeItemName(xMatch[2]),
        quantity: normalizeQuantity(xMatch[1])
      };
    } else if (xMatch[3] && xMatch[4]) { // "item x number" format
      const itemName = xMatch[3].trim();
      if (itemName) {
        return {
          name: normalizeItemName(itemName),
          quantity: normalizeQuantity(xMatch[4])
        };
      }
    }
  }
}


    // Try other patterns in sequence if not an x-format
    for (const [format, pattern] of Object.entries(INPUT_PATTERNS)) {
      const result = tryParseFormat(trimmedLine, format, pattern);
      if (result) return result;
    }
  
    // If we get here and the line contains 'x', try the fallback x handling
  
    // Enhanced single item handling
    // Check if the entire line is just a name with no numbers
    if (!/\d/.test(trimmedLine)) {
      return {
        name: normalizeItemName(trimmedLine),
        quantity: 1
      };
    }
  
    // If the line contains numbers but doesn't match any pattern,
    // try to separate the last number group as quantity
  const spaceSeparatedNumberMatch = trimmedLine.match(/^(.*?)\s+(\d+)$/);
  if (spaceSeparatedNumberMatch) {
    const [_, itemPart, quantityPart] = spaceSeparatedNumberMatch;
    if (itemPart.trim()) {
      return {
        name: normalizeItemName(itemPart),
        quantity: normalizeQuantity(quantityPart)
      };
    }
  }
  
    // If everything else fails, treat as single item with quantity 1
    return {
      name: normalizeItemName(trimmedLine),
      quantity: 1
    };
  };

  const consolidateItems = (items) => {
    const itemMap = new Map();
    
    items.forEach(item => {
      if (!item) return;
      
      const normalizedName = item.name.toLowerCase();
      if (itemMap.has(normalizedName)) {
        itemMap.set(normalizedName, {
          name: item.name, // Keep original case from first occurrence
          quantity: itemMap.get(normalizedName).quantity + item.quantity
        });
      } else {
        itemMap.set(normalizedName, item);
      }
    });

    return Array.from(itemMap.values());
  };

  const parseInput = (input) => {
    const lines = input
      .split('\n')
      .filter(line => line.trim())
      .filter(line => !line.toLowerCase().includes('total:'));

    const parsedItems = lines
      .map(parseItemLine)
      .flat() // Flatten arrays from module+attachment parsing
      .filter(item => item && item.name);

    return consolidateItems(parsedItems);
  };

  const handleSubmit = async () => {
    try {

      setLoading(true); // Start loading
      setError('');

      // Track submit button click
      trackEvent('button_click', {
        event_category: 'engagement',
        event_label: 'appraisal_submit',
        market_hub: selectedMarket,
        input_characters: rawInput.length,
        button_text: 'Submit',
        value: 1  // GA4 likes numeric values
      });

      const items = parseInput(rawInput);
      
      if (items.length === 0) {
        setError('No valid items found in input. Please check your format and try again.');
        setLoading(false); // Stop loading

        // Track validation error
        trackEvent('form_error', {
          event_category: 'error',
          event_label: 'validation_error',
          error_type: 'no_valid_items',
          market_hub: selectedMarket,
          value: 0
        });
        return;
      }

      const response = await appraiseItems(items, selectedMarket, true, rawInput);
      onResults(response);
      setError('');

      // Track successful appraisal
      trackEvent('form_submission_success', {
        event_category: 'conversion',
        event_label: 'appraisal_complete',
        market_hub: selectedMarket,
        items_count: items.length,
        appraisal_id: response.appraisal_id,
        value: response.total_sell_value
      });
      onAppraisalComplete(response);
    } catch (err) {
      console.error('Error during appraisal:', err);
      setError('Failed to process items. Please try again or check your input format.');
      
      // Track error with more detailed parameters
      trackEvent('form_error', {
        event_category: 'error',
        event_label: 'processing_error',
        error_type: 'api_error',
        market_hub: selectedMarket,
        error_message: err.message || 'Unknown error',
        value: 0
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };
  const handleCompare = async () => {
    try {
      setLoading(true); // Start loading
      setError('');

      // Track compare button click
      trackEvent('button_click', {
        event_category: 'engagement',
        event_label: 'compare_submit',
        market_hub1: selectedMarket,
        market_hub2: selectedMarket2,
        input_characters: rawInput.length,
        button_text: 'Compare',
        value: 1  // GA4 likes numeric values
      });

      const items = parseInput(rawInput);
      
      if (items.length === 0) {
        setError('No valid items found in input. Please check your format and try again.');
        setLoading(false); // Stop loading

        // Track validation error
        trackEvent('form_error', {
          event_category: 'error',
          event_label: 'validation_error',
          error_type: 'no_valid_items',
          market_hub1: selectedMarket,
          market_hub2: selectedMarket2,
          value: 0
        });
        return;
      }

      const response = await compareMarkets(items, selectedMarket, selectedMarket2, true, rawInput);
      onResults(response);
      setError('');

      // Track successful comparison
      trackEvent('form_submission_success', {
        event_category: 'conversion',
        event_label: 'comparison_complete',
        market_hub1: selectedMarket,
        market_hub2: selectedMarket2,
        items_count: items.length,
        comparison_id: response.comparison_id,
        value: response.total_sell_value_market1 + response.total_sell_value_market2
      });
      onAppraisalComplete(response);
    } catch (err) {
      console.error('Error during comparison:', err);
      setError('Failed to process items. Please try again or check your input format.');
      
      // Track error with more detailed parameters
      trackEvent('form_error', {
        event_category: 'error',
        event_label: 'processing_error',
        error_type: 'api_error',
        market_hub1: selectedMarket,
        market_hub2: selectedMarket2,
        error_message: err.message || 'Unknown error',
        value: 0
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <div className="bulk-input-form">
      <h2>Submit Items for Appraisal</h2>
      <div className="market-selector-group flex gap-4 flex-wrap">
        <MarketSelector
          selectedMarket={selectedMarket}
          onMarketChange={setSelectedMarket}
          icon="location" 
          label="Primary Market"
        />
        <div className="comparison-arrow self-center text-gray-400">→</div>
        <MarketSelector
          selectedMarket={selectedMarket2}
          onMarketChange={setSelectedMarket2}
          icon="compare"
          label="Compare With"
        />
      </div>
      <textarea
        value={rawInput}
        onChange={(e) => setRawInput(e.target.value)}
        placeholder={`Enter items and quantities in any format (e.g., 'Item Name x Quantity' or 'Quantity Item Name')
Items with special characters (-,.'"()*), Or ends with digits please add Quantity (e.g., 'Zainou 'Beancounter' Industry BX-802 x13')`}
        rows={8}
        cols={60}
        className="textarea"
      />
      {error && <p className="error">{error}</p>}
      <div className="space-y-8">
        {showYieldParams && (
          <div className="relative border border-orange-400 rounded-lg p-6 mb-4">
            <span className="absolute -top-3 left-3 px-2  text-sm font-mono text-gray-100 bg-stone-950">
              Processing Settings
            </span>
            
            <div className="flex">
              {/* Left side - Processing settings dropdowns */}
              <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Rig Modifier</label>
                  <select 
                    value={yieldParams.Rm} 
                    onChange={(e) => setYieldParams({...yieldParams, Rm: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 md:px-4 md:py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                    text-gray-100 placeholder-gray-400 focus:ring-3 focus:ring-orange-700
                    font-mono hover:border-orange-400 text-sm md:text-base"
                  >
                    <option value={0}>No Rig</option>
                    <option value={1}>T1 Rig</option>
                    <option value={3}>T2 Rig</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Security Status</label>
                  <select 
                    value={yieldParams.Sec} 
                    onChange={(e) => setYieldParams({...yieldParams, Sec: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 md:px-4 md:py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                    text-gray-100 placeholder-gray-400 focus:ring-3 focus:ring-orange-700
                    font-mono hover:border-orange-400 text-sm md:text-base"
                  >
                    <option value={0}>Highsec</option>
                    <option value={0.06}>Lowsec</option>
                    <option value={0.12}>Nullsec/WH</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Structure Type</label>
                  <select 
                    value={yieldParams.Sm} 
                    onChange={(e) => setYieldParams({...yieldParams, Sm: parseFloat(e.target.value)})}
                    className="w-auto px-4 py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                 text-gray-100 placeholder-gray-400 focus:ring-3 focus:ringww-orange-700
                  font-mono hover:border-orange-400"
                  >
                    <option value={0}>Other Structures</option>
                    <option value={0.02}>Athanor</option>
                    <option value={0.055}>Tatara</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Implant Modifier</label>
                  <select 
                    value={yieldParams.Im} 
                    onChange={(e) => setYieldParams({...yieldParams, Im: parseFloat(e.target.value)})}
                    className="w-auto px-4 py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                 text-gray-100 placeholder-gray-400 focus:ring-3 focus:ringww-orange-700
                  font-mono hover:border-orange-400"
                  >
                    <option value={0}>None</option>
                    <option value={0.01}>RX-801</option>
                    <option value={0.02}>RX-802</option>
                    <option value={0.04}>RX-804</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Reprocessing</label>
                  <select 
                    value={yieldParams.R} 
                    onChange={(e) => setYieldParams({...yieldParams, R: parseInt(e.target.value)})}
                    className="w-auto px-4 py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                 text-gray-100 placeholder-gray-400 focus:ring-3 focus:ringww-orange-700
                  font-mono hover:border-orange-400"
                  >
                    <option value={0}>Not Trained</option>
                    <option value={1}>I</option>
                    <option value={2}>II</option>
                    <option value={3}>III</option>
                    <option value={4}>IV</option>
                    <option value={5}>V</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Reprocessing Efficiency</label>
                  <select 
                    value={yieldParams.Re} 
                    onChange={(e) => setYieldParams({...yieldParams, Re: parseInt(e.target.value)})}
                    className="w-auto px-4 py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                 text-gray-100 placeholder-gray-400 focus:ring-3 focus:ringww-orange-700
                  font-mono hover:border-orange-400"
                  >
                    <option value={0}>Not Trained</option>
                    <option value={1}>I</option>
                    <option value={2}>II</option>
                    <option value={3}>III</option>
                    <option value={4}>IV</option>
                    <option value={5}>V</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-mono text-gray-700 dark:text-gray-300">Specific Ore Processing*</label>
                  <select 
                    value={yieldParams.Op} 
                    onChange={(e) => setYieldParams({...yieldParams, Op: parseInt(e.target.value)})}
                    className="w-auto px-4 py-2 bg-neutral-900 border border-gray-600 rounded-lg 
                 text-gray-100 placeholder-gray-400 focus:ring-3 focus:ringww-orange-700
                  font-mono hover:border-orange-400"
                  >
                    <option value={0}>Not Trained</option>
                    <option value={1}>I</option>
                    <option value={2}>II</option>
                    <option value={3}>III</option>
                    <option value={4}>IV</option>
                    <option value={5}>V</option>
                  </select>
                
                </div>
                <div className="block text-xs font-mono text-gray-400 ">  "Specific Ore Processing" Skill affects Special Ore refining, Scrap metal processing and Gas decompressing.
                </div>
              </div>

              {/* Right side - Total Yield display */}
              <div className="mt-4 lg:mt-0 lg:ml-4 flex items-center justify-center lg:justify-start">
                <div className="p-2 md:p-3 rounded flex border border-gray-600 flex-col items-center lg:items-end">
                  <span className="text-sm font-mono text-gray-300">Total Yield</span>
                  <span className="text-base md:text-lg font-bold text-orange-500">
                    {(() => {
                      const yield_factor = (
                        (50 + yieldParams.Rm) *
                        (1 + yieldParams.Sec) *
                        (1 + yieldParams.Sm) *
                        (1 + (yieldParams.R * 0.03)) *
                        (1 + (yieldParams.Re * 0.02)) *
                        (1 + (yieldParams.Op * 0.02)) *
                        (1 + yieldParams.Im)
                      ) / 100;
                      return `${(yield_factor * 100).toFixed(3)}%`;
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button 
            className="submit-button w-full sm:w-auto" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <HashLoader size={16} color="#ffffff" />
            ) : (
              'Appraise'
            )}
          </button>
          <button 
            className="submit-button w-full sm:w-auto" 
            onClick={handleCompare}
            disabled={loading}
          >
            {loading ? (
              <HashLoader size={20} color="#ffffff" />
            ) : (
              'Shopping List'
            )}
          </button>
          <button 
            className={`submit-button w-full sm:w-auto ${showYieldParams ? 'bg-blue-600' : ''}`}
            onClick={async () => {
              if (!showYieldParams) {
                setShowYieldParams(true);
                return;
              }
              
              try {
                setLoading(true);
                setError('');

                const items = parseInput(rawInput);
                
                if (items.length === 0) {
                  setError('No valid items found in input. Please check your format and try again.');
                  return;
                }

                const response = await processItems(items, selectedMarket, yieldParams, true, rawInput);
                onResults(response);
                setError('');

            // Track successful processing
            trackEvent('form_submission_success', {
              event_category: 'conversion',
              event_label: 'process_complete',
              market_hub: selectedMarket,
              items_count: items.length,
              processed_id: response.appraisal_id
            });
            onAppraisalComplete({
              processed_id: response.appraisal_id,
              appraisal_id: null, // Ensure we don't trigger standard appraisal navigation
              ...response
            });
              } catch (err) {
                console.error('Error during processing:', err);
                setError('Failed to process items. Please try again.');
                
                trackEvent('form_error', {
                  event_category: 'error',
                  event_label: 'processing_error',
                  error_type: 'api_error',
                  market_hub: selectedMarket,
                  error_message: err.message || 'Unknown error'
                });
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            {loading ? (
              <HashLoader size={16} color="#ffffff" />
            ) : (
              showYieldParams ? 'Process Items' : 'Show Processing Options'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkInputForm;
